// src/components/Account/DataRetention.js - Modernized
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Modal, ProgressBar } from 'react-bootstrap';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import './ModernAccountStyles.css';

/**
 * Modern component for managing data retention settings in user account
 */
const DataRetention = () => {
	// Auth context
	const { user } = useAuth();

	// State for retention settings
	const [settings, setSettings] = useState({
		transactionRetentionDays: 730,
		insightRetentionDays: 365,
		emailNotifications: true,
		analyticalDataUse: true
	});

	// UI state
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [showExportModal, setShowExportModal] = useState(false);
	const [exportLoading, setExportLoading] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [exportFormat, setExportFormat] = useState('json');

	// Load current retention settings
	useEffect(() => {
		const fetchSettings = async () => {
			try {
				setLoading(true);

				const response = await api.get('/v1/data/retention-settings');

				if (response.data && response.data.success) {
					setSettings(response.data.data.settings);
				} else {
					logger.warn('Failed to load retention settings, using defaults');
				}
			} catch (err) {
				logger.error('Error loading retention settings:', err);
				setError('Failed to load your data retention settings');
			} finally {
				setLoading(false);
			}
		};

		if (user) {
			fetchSettings();
		}
	}, [user]);

	// Handle settings change
	const handleSettingsChange = (field, value) => {
		setSettings(prev => ({
			...prev,
			[field]: value
		}));
	};

	// Save settings
	const handleSaveSettings = async () => {
		try {
			setSaving(true);
			setError('');
			setSuccess('');

			const response = await api.put('/v1/data/retention-settings', settings);

			if (response.data && response.data.success) {
				setSuccess('Data retention settings updated successfully');
			} else {
				throw new Error(response.data?.message || 'Failed to update settings');
			}
		} catch (err) {
			logger.error('Error saving retention settings:', err);
			setError(err.message || 'Failed to update data retention settings');
		} finally {
			setSaving(false);
		}
	};

	// Handle data export
	const handleExportData = async (format = 'json') => {
		try {
			setExportLoading(true);
			setError('');
			logger.info(`Starting data export in ${format} format`);

			const token = localStorage.getItem('token');

			if (!token) {
				throw new Error('Authentication token not found. Please log in again.');
			}

			const responseType = format === 'pdf' ? 'arraybuffer' : 'blob';

			const response = await api.get(`/v1/data/export?format=${format}`, {
				responseType,
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});

			const contentType = format === 'pdf' ? 'application/pdf' : 'application/json';
			const blob = new Blob([response.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.download = `financial-data-export-${new Date().toISOString().split('T')[0]}.${format}`;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			window.URL.revokeObjectURL(url);

			setTimeout(() => {
				setShowExportModal(false);
				setExportLoading(false);
				setSuccess(`Data export completed successfully in ${format.toUpperCase()} format.`);
			}, 1000);
		} catch (err) {
			logger.error('Error exporting data:', err);
			setError(err.message || 'Failed to export your data');
			setExportLoading(false);
		}
	};

	// Handle account deletion request
	const handleAccountDeletion = async () => {
		try {
			setLoading(true);

			const response = await api.post('/v1/data/close-account');

			if (response.data && response.data.success) {
				setSuccess('Your account closure has been initiated. Your account will be fully deleted after 30 days.');
				setShowDeleteModal(false);
			} else {
				throw new Error(response.data?.message || 'Failed to initiate account closure');
			}
		} catch (err) {
			logger.error('Error initiating account closure:', err);
			setError(err.message || 'Failed to initiate account closure');
		} finally {
			setLoading(false);
			setShowDeleteModal(false);
		}
	};

	return (
		<div className="modern-data-retention">
			<div className="modern-page-header">
				<div className="header-glass-bg"></div>
				<div className="header-content">
					<div className="header-icon-wrapper">
						<div className="header-main-icon">
							<i className="bi bi-shield-check"></i>
						</div>
						<div className="header-text-content">
							<h1 className="page-title">Data Management</h1>
							<p className="page-subtitle">Control your data retention and privacy settings</p>
						</div>
					</div>
				</div>
			</div>

			<div className="modern-content-wrapper">
				{/* Modern Alerts */}
				{error && (
					<div className="modern-alert error">
						<div className="alert-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Error:</strong> {error}
						</div>
						<button className="alert-close" onClick={() => setError('')}>
							<i className="bi bi-x"></i>
						</button>
					</div>
				)}

				{success && (
					<div className="modern-alert success">
						<div className="alert-icon">
							<i className="bi bi-check-circle-fill"></i>
						</div>
						<div className="alert-content">
							<strong>Success:</strong> {success}
						</div>
						<button className="alert-close" onClick={() => setSuccess('')}>
							<i className="bi bi-x"></i>
						</button>
					</div>
				)}

				{loading ? (
					<div className="modern-loading-card">
						<div className="modern-spinner">
							<div className="spinner-ring"></div>
							<div className="spinner-ring"></div>
							<div className="spinner-ring"></div>
						</div>
						<p className="loading-text">Loading your data settings...</p>
					</div>
				) : (
					<div className="modern-cards-grid">
						{/* Data Retention Settings Card */}
						<div className="modern-card main-card">
							<div className="card-header">
								<div className="card-header-content">
									<div className="card-icon">
										<i className="bi bi-clock-history"></i>
									</div>
									<div>
										<h3>Data Retention Preferences</h3>
										<p>Customize how long we store your financial data</p>
									</div>
								</div>
							</div>

							<div className="card-body">
								<div className="retention-settings">
									<div className="setting-group">
										<div className="setting-header">
											<div className="setting-icon">
												<i className="bi bi-receipt"></i>
											</div>
											<div className="setting-info">
												<h4>Transaction History</h4>
												<p>How long we store your transaction data for analysis and insights</p>
											</div>
										</div>

										<div className="setting-control">
											<select
												className="modern-select"
												value={settings.transactionRetentionDays}
												onChange={(e) => handleSettingsChange('transactionRetentionDays', parseInt(e.target.value))}
											>
												<option value="90">3 months</option>
												<option value="180">6 months</option>
												<option value="365">1 year</option>
												<option value="730">2 years (recommended)</option>
											</select>
										</div>
									</div>

									<div className="setting-group">
										<div className="setting-header">
											<div className="setting-icon">
												<i className="bi bi-lightbulb"></i>
											</div>
											<div className="setting-info">
												<h4>Financial Insights</h4>
												<p>Duration for storing AI-generated financial analysis and recommendations</p>
											</div>
										</div>

										<div className="setting-control">
											<select
												className="modern-select"
												value={settings.insightRetentionDays}
												onChange={(e) => handleSettingsChange('insightRetentionDays', parseInt(e.target.value))}
											>
												<option value="90">3 months</option>
												<option value="180">6 months</option>
												<option value="365">1 year (recommended)</option>
												<option value="730">2 years</option>
											</select>
										</div>
									</div>

									<div className="setting-group">
										<div className="setting-header">
											<div className="setting-icon">
												<i className="bi bi-envelope"></i>
											</div>
											<div className="setting-info">
												<h4>Email Notifications</h4>
												<p>Receive important updates about data retention and account activity</p>
											</div>
										</div>

										<div className="setting-control">
											<label className="modern-toggle">
												<input
													type="checkbox"
													checked={settings.emailNotifications}
													onChange={(e) => handleSettingsChange('emailNotifications', e.target.checked)}
												/>
												<span className="toggle-slider"></span>
												<span className="toggle-label">
													{settings.emailNotifications ? 'Enabled' : 'Disabled'}
												</span>
											</label>
										</div>
									</div>

									<div className="setting-group">
										<div className="setting-header">
											<div className="setting-icon">
												<i className="bi bi-graph-up"></i>
											</div>
											<div className="setting-info">
												<h4>Insights Improvement</h4>
												<p>Allow anonymized data usage to enhance our AI insights (no personal info shared)</p>
											</div>
										</div>

										<div className="setting-control">
											<label className="modern-toggle">
												<input
													type="checkbox"
													checked={settings.analyticalDataUse}
													onChange={(e) => handleSettingsChange('analyticalDataUse', e.target.checked)}
												/>
												<span className="toggle-slider"></span>
												<span className="toggle-label">
													{settings.analyticalDataUse ? 'Enabled' : 'Disabled'}
												</span>
											</label>
										</div>
									</div>
								</div>

								<div className="save-section">
									<button
										className="save-btn"
										onClick={handleSaveSettings}
										disabled={saving}
									>
										{saving ? (
											<>
												<div className="btn-spinner"></div>
												<span>Saving...</span>
											</>
										) : (
											<>
												<i className="bi bi-check-circle"></i>
												<span>Save Preferences</span>
											</>
										)}
									</button>
								</div>
							</div>
						</div>

						{/* Data Controls Card */}
						<div className="modern-card">
							<div className="card-header">
								<div className="card-header-content">
									<div className="card-icon controls">
										<i className="bi bi-gear"></i>
									</div>
									<div>
										<h3>Your Data Controls</h3>
										<p>Export or delete your personal information</p>
									</div>
								</div>
							</div>

							<div className="card-body">
								<div className="data-controls">
									<div className="control-item">
										<div className="control-icon export">
											<i className="bi bi-download"></i>
										</div>
										<div className="control-content">
											<h4>Export Your Data</h4>
											<p>Download a complete copy of all your financial data and insights</p>
											<button
												className="control-btn primary"
												onClick={() => setShowExportModal(true)}
											>
												<i className="bi bi-download"></i>
												Export Data
											</button>
										</div>
									</div>

									<div className="control-divider"></div>

									<div className="control-item">
										<div className="control-icon delete">
											<i className="bi bi-trash"></i>
										</div>
										<div className="control-content">
											<h4>Close Account</h4>
											<p>Permanently delete your account and all associated data</p>
											<button
												className="control-btn danger"
												onClick={() => setShowDeleteModal(true)}
											>
												<i className="bi bi-trash"></i>
												Close Account
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Privacy Policy Info Card */}
						<div className="modern-card">
							<div className="card-header">
								<div className="card-header-content">
									<div className="card-icon info">
										<i className="bi bi-info-circle"></i>
									</div>
									<div>
										<h3>Data Handling Policy</h3>
										<p>How we protect and manage your information</p>
									</div>
								</div>
							</div>

							<div className="card-body">
								<div className="policy-info">
									<div className="info-item">
										<div className="info-icon">
											<i className="bi bi-shield-lock"></i>
										</div>
										<div className="info-content">
											<h4>Data Security</h4>
											<p>All data is encrypted in transit and at rest using industry-standard security measures</p>
										</div>
									</div>

									<div className="info-item">
										<div className="info-icon">
											<i className="bi bi-clock"></i>
										</div>
										<div className="info-content">
											<h4>Automatic Cleanup</h4>
											<p>Inactive accounts receive warnings after 12 months and are deleted after 15 months</p>
										</div>
									</div>

									<div className="policy-link">
										<p>For complete details, review our <PrivacyPolicyModal /></p>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{/* Enhanced Data Export Modal */}
			<Modal
				show={showExportModal}
				onHide={() => !exportLoading && setShowExportModal(false)}
				centered
				backdrop="static"
				className="modern-modal"
			>
				<Modal.Header closeButton={!exportLoading} className="modern-modal-header">
					<Modal.Title className="modern-modal-title">
						<div className="modal-icon">
							<i className="bi bi-download"></i>
						</div>
						Export Your Data
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modern-modal-body">
					{error ? (
						<div className="modern-alert error">
							<div className="alert-icon">
								<i className="bi bi-exclamation-triangle-fill"></i>
							</div>
							<div className="alert-content">{error}</div>
						</div>
					) : (
						<>
							<div className="export-info">
								<p>This will download a complete copy of your data, including:</p>
								<div className="data-types">
									<div className="data-type">
										<i className="bi bi-person"></i>
										<span>Account information</span>
									</div>
									<div className="data-type">
										<i className="bi bi-bank"></i>
										<span>Connected bank details</span>
									</div>
									<div className="data-type">
										<i className="bi bi-receipt"></i>
										<span>Transaction history</span>
									</div>
									<div className="data-type">
										<i className="bi bi-lightbulb"></i>
										<span>Financial insights</span>
									</div>
								</div>
							</div>

							<div className="format-selection">
								<label className="modern-label">Export Format</label>
								<select
									className="modern-select"
									value={exportFormat}
									onChange={(e) => setExportFormat(e.target.value)}
									disabled={exportLoading}
								>
									<option value="json">JSON (Raw Data)</option>
									<option value="pdf">PDF (Formatted Report)</option>
								</select>
								<div className="format-description">
									{exportFormat === 'json'
										? 'JSON format includes all raw data in a machine-readable format.'
										: 'PDF format provides a readable report with formatted data and visualizations.'}
								</div>
							</div>

							{exportLoading && (
								<div className="export-progress">
									<div className="progress-header">
										<strong>{exportFormat === 'pdf' ? 'Generating PDF report...' : 'Preparing your data...'}</strong>
									</div>
									<div className="progress-bar">
										<div className="progress-fill animated"></div>
									</div>
								</div>
							)}

							<div className="security-notice">
								<div className="notice-icon">
									<i className="bi bi-shield-exclamation"></i>
								</div>
								<p>For security reasons, some sensitive data may be excluded from the export.</p>
							</div>
						</>
					)}
				</Modal.Body>
				<Modal.Footer className="modern-modal-footer">
					<button
						className="modern-btn modern-btn-secondary"
						onClick={() => setShowExportModal(false)}
						disabled={exportLoading}
					>
						{success ? 'Close' : 'Cancel'}
					</button>
					<button
						className="modern-btn modern-btn-primary"
						onClick={() => handleExportData(exportFormat)}
						disabled={exportLoading}
					>
						{exportLoading ? (
							<>
								<div className="btn-spinner"></div>
								{exportFormat === 'pdf' ? 'Generating...' : 'Exporting...'}
							</>
						) : (
							<>
								<i className="bi bi-download"></i>
								Download {exportFormat.toUpperCase()}
							</>
						)}
					</button>
				</Modal.Footer>
			</Modal>

			{/* Account Deletion Modal */}
			<Modal
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				backdrop="static"
				keyboard={false}
				centered
				className="modern-modal danger-modal"
			>
				<Modal.Header closeButton className="modern-modal-header">
					<Modal.Title className="modern-modal-title">
						<div className="modal-icon danger">
							<i className="bi bi-exclamation-triangle"></i>
						</div>
						Close Account
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="modern-modal-body">
					<div className="danger-warning">
						<div className="warning-icon">
							<i className="bi bi-exclamation-triangle-fill"></i>
						</div>
						<div className="warning-content">
							<strong>Warning:</strong> This action cannot be undone after the grace period.
						</div>
					</div>

					<div className="deletion-process">
						<h4>What happens when you close your account:</h4>
						<div className="process-steps">
							<div className="step">
								<div className="step-number">1</div>
								<div className="step-content">Your account will be immediately deactivated</div>
							</div>
							<div className="step">
								<div className="step-number">2</div>
								<div className="step-content">All API access will be revoked</div>
							</div>
							<div className="step">
								<div className="step-number">3</div>
								<div className="step-content">You'll have a 30-day grace period to reactivate</div>
							</div>
							<div className="step">
								<div className="step-number">4</div>
								<div className="step-content">After 30 days, all data will be permanently deleted</div>
							</div>
						</div>
					</div>

					<div className="confirmation-question">
						<strong>Are you sure you want to close your account?</strong>
					</div>
				</Modal.Body>
				<Modal.Footer className="modern-modal-footer">
					<button
						className="modern-btn modern-btn-secondary"
						onClick={() => setShowDeleteModal(false)}
					>
						Cancel
					</button>
					<button
						className="modern-btn modern-btn-danger"
						onClick={handleAccountDeletion}
						disabled={loading}
					>
						{loading ? (
							<>
								<div className="btn-spinner"></div>
								Processing...
							</>
						) : (
							<>
								<i className="bi bi-trash"></i>
								Close Account
							</>
						)}
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default DataRetention;